import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useCharacters } from '@/features/characters';
import { useOrganizations } from '@/features/organizations';
import { ORGANIZATION_HIERARCHY_FEATURE_KEY } from '@/features/organizations/utils/organization-hierarchy-feature';
import { useSubscription } from '@/hooks/useSubscription';
import type { Organization } from '@/lib/types';
import { hasValue } from '@/lib/utils/has-value';

import { FieldDisplay } from './field-display';

interface SectionOrganizationMainProps {
  data: Partial<Organization>;
}

function OrganizationHierarchyFields({ data }: SectionOrganizationMainProps) {
  const { t } = useTranslation();
  const { characters } = useCharacters();
  const { organizations } = useOrganizations();

  const characterNameById = useMemo(() => (
    new Map(characters.map((character) => [character.id, character.name]))
  ), [characters]);

  const titleById = useMemo(() => (
    new Map((data.organizationTitles || []).map((title) => [title.id, title.name]))
  ), [data.organizationTitles]);

  const organizationNameById = useMemo(() => (
    new Map(organizations.map((organization) => [organization.id, organization.name]))
  ), [organizations]);

  const parentOrganizationNames = useMemo(() => (
    (data.parentOrganizationIds || []).map((organizationId) => organizationNameById.get(organizationId) || organizationId)
  ), [data.parentOrganizationIds, organizationNameById]);

  const childOrganizationNames = useMemo(() => (
    (data.childOrganizationIds || []).map((organizationId) => {
      const organizationName = organizationNameById.get(organizationId) || organizationId;
      const title = data.childOrganizationTitles?.[organizationId];

      if (!title) {
        return organizationName;
      }

      return t('entity_form.organizations.membership.member_display', {
        characterName: organizationName,
        titleName: title,
      });
    })
  ), [data.childOrganizationIds, data.childOrganizationTitles, organizationNameById, t]);

  const memberSummaries = useMemo(() => (
    (data.organizationMembers || []).map((member) => {
      const characterName = characterNameById.get(member.characterId) || member.characterId;
      const title = member.title || (member.titleId ? titleById.get(member.titleId) : '');

      if (!title) {
        return characterName;
      }

      return t('entity_form.organizations.membership.member_display', {
        characterName,
        titleName: title,
      });
    })
  ), [characterNameById, data.organizationMembers, t, titleById]);

  return (
    <>
      {parentOrganizationNames.length > 0 && (
        <Grid size={{ xs: 12, md: 6 }}>
          <FieldDisplay label={t('entity_form.organizations.membership.parent_organizations_heading')} value={parentOrganizationNames} />
        </Grid>
      )}
      {childOrganizationNames.length > 0 && (
        <Grid size={{ xs: 12, md: 6 }}>
          <FieldDisplay label={t('entity_form.organizations.membership.child_organizations_heading')} value={childOrganizationNames} />
        </Grid>
      )}
      {memberSummaries.length > 0 && (
        <Grid size={{ xs: 12, md: 6 }}>
          <FieldDisplay label={t('entity_form.organizations.membership.members_heading')} value={memberSummaries} />
        </Grid>
      )}
    </>
  );
}

/**
 * Main identity section for Organization view pages.
 */
export const SectionOrganizationMain = ({ data }: SectionOrganizationMainProps) => {
  const { t } = useTranslation();
  const { hasFeature } = useSubscription();
  const isOrganizationHierarchyEnabled = hasFeature(ORGANIZATION_HIERARCHY_FEATURE_KEY);

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h2" sx={{ mb: 2 }}>
        {t('entity_form.organizations.labels.main_info_title')}
      </Typography>
      <Grid container spacing={3}>
        {hasValue(data.type) && (
          <Grid size={{ xs: 12, md: 6 }}>
            <FieldDisplay label={t('entity_form.organizations.labels.type')} value={data.type} />
          </Grid>
        )}
        {hasValue(data.shortDescription) && (
          <Grid size={{ xs: 12 }}>
            <FieldDisplay label={t('entity_form.organizations.labels.short_desc')} value={data.shortDescription} type="textarea" />
          </Grid>
        )}
        {isOrganizationHierarchyEnabled && <OrganizationHierarchyFields data={data} />}
      </Grid>
    </Paper>
  );
};
